import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  PhoneNumberDraft,
  PhoneNumberLabel,
  PhoneNumberStatus,
} from '@employ-engage/data';

/** The most numbers one person can have; the API enforces the same. */
export const MAX_PHONE_NUMBERS = 5;

export const PHONE_NUMBER_LABELS: PhoneNumberLabel[] = ['MOBILE', 'WORK', 'HOME', 'OTHER'];

/**
 * Why a number in the list can't be saved, or null. Numbers without a
 * country code are read as US, as the API reads them.
 */
export const phoneNumberProblem = (
  number: string,
  others: string[]
): 'required' | 'invalid' | 'duplicate' | null => {
  if (!number.trim()) return 'required';
  const parsed = parsePhoneNumberFromString(number.trim(), 'US');
  if (!parsed?.isValid()) return 'invalid';
  const others164 = others.map((other) => parsePhoneNumberFromString(other.trim(), 'US')?.number);
  return others164.includes(parsed.number) ? 'duplicate' : null;
};

/** Whether every number in the list could be saved. */
export const phoneNumberListIsValid = (list: PhoneNumberDraft[]) =>
  list.every(
    (item, index) =>
      phoneNumberProblem(
        item.number,
        list.filter((_, other) => other !== index).map((o) => o.number)
      ) === null
  );

const STATUS_STYLE: Record<PhoneNumberStatus, string> = {
  TEXTS_ON: 'bg-green-50 text-green-800 ring-green-600/20',
  NEEDS_CONFIRMING: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
  TEXTS_OFF: 'bg-gray-50 text-gray-700 ring-gray-500/20',
  REPLIED_STOP: 'bg-red-50 text-red-800 ring-red-600/20',
};

export const PhoneStatusBadge = ({ status }: { status: PhoneNumberStatus }) => {
  const { t } = useTranslation(['common']);
  const text: Record<PhoneNumberStatus, string> = {
    TEXTS_ON: t('Texts on'),
    NEEDS_CONFIRMING: t('Needs confirming'),
    TEXTS_OFF: t('Texts off'),
    REPLIED_STOP: t('Replied STOP'),
  };
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-1 text-base font-medium ring-1 ring-inset ${STATUS_STYLE[status]}`}
    >
      {text[status]}
    </span>
  );
};

export const usePhoneLabelText = () => {
  const { t } = useTranslation(['common']);
  return (label: PhoneNumberLabel) =>
    // Their own keys: "Home" alone is already translated as a home page.
    ({
      MOBILE: t('phone-label-mobile'),
      WORK: t('phone-label-work'),
      HOME: t('phone-label-home'),
      OTHER: t('phone-label-other'),
    })[label];
};

export interface PhoneNumberListProps {
  /** The list as it stands. Controlled: every change comes back through `onChange`. */
  value: PhoneNumberDraft[];
  onChange: (value: PhoneNumberDraft[]) => void;
  /** The texting status of saved numbers, by id, shown beside each one. Employees only. */
  statusById?: Record<string, PhoneNumberStatus>;
  /** The group's visible name, e.g. "Phone numbers". */
  legend: string;
  /** Shown under the legend. */
  description?: string;
  disabled?: boolean;
}

/**
 * Editing a person's phone numbers: one row per number, each with a label,
 * up to five, and a problem shown beside the number that has it.
 *
 * Accessibility: the rows sit in a fieldset named by its legend; each input
 * and select is labelled by its row ("Phone number 2", "Label for phone
 * number 2"); a problem is tied to its input with aria-describedby and
 * aria-invalid; the remove button says which number it removes.
 */
export function PhoneNumberList({
  value,
  onChange,
  statusById,
  legend,
  description,
  disabled,
}: PhoneNumberListProps) {
  const { t } = useTranslation(['common']);
  const labelText = usePhoneLabelText();
  const baseId = useId();

  const update = (index: number, patch: Partial<PhoneNumberDraft>) =>
    onChange(value.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const problemText = {
    required: t('Enter a phone number, or remove this row.'),
    invalid: t('Please enter a valid phone number with country code (e.g. +12345678900)'),
    duplicate: t('This number is already on the list.'),
  };

  return (
    <fieldset className="col-span-6" disabled={disabled}>
      <legend className="block text-base font-medium text-gray-700">{legend}</legend>
      {description && <p className="mt-1 text-base text-gray-500">{description}</p>}

      <ul className="mt-2 space-y-3">
        {value.map((item, index) => {
          const position = index + 1;
          const inputId = `${baseId}-number-${index}`;
          const problemId = `${baseId}-problem-${index}`;
          const problem = phoneNumberProblem(
            item.number,
            value.filter((_, other) => other !== index).map((o) => o.number)
          );
          // Only once something has been typed: an empty new row isn't an error yet.
          const showProblem = !!problem && !(problem === 'required' && !item.id && item.number === '');
          const status = item.id ? statusById?.[item.id] : undefined;
          return (
            <li key={item.id ?? `new-${index}`} className="flex flex-wrap items-start gap-2">
              <div className="min-w-[12rem] flex-1">
                <label htmlFor={inputId} className="sr-only">
                  {t('Phone number {{position}}', { position })}
                </label>
                <input
                  id={inputId}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={item.number}
                  onChange={(e) => update(index, { number: e.target.value })}
                  aria-invalid={showProblem || undefined}
                  aria-describedby={showProblem ? problemId : undefined}
                  placeholder={t('+1 234 567 8900 (add the country code)')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base min-h-[44px] min-w-[44px]"
                />
                {showProblem && (
                  <p id={problemId} className="mt-1 text-base text-red-600">
                    {problemText[problem]}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor={`${inputId}-label`} className="sr-only">
                  {t('Label for phone number {{position}}', { position })}
                </label>
                <select
                  id={`${inputId}-label`}
                  value={item.label}
                  onChange={(e) => update(index, { label: e.target.value as PhoneNumberLabel })}
                  className="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base min-h-[44px] min-w-[44px]"
                >
                  {PHONE_NUMBER_LABELS.map((label) => (
                    <option key={label} value={label}>
                      {labelText(label)}
                    </option>
                  ))}
                </select>
              </div>
              {status && (
                <div className="pt-1.5">
                  <PhoneStatusBadge status={status} />
                </div>
              )}
              <button
                type="button"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                className="rounded-md px-2 py-1.5 text-base font-medium text-red-700 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600 min-h-[44px] min-w-[44px]"
                aria-label={t('Remove phone number {{position}}', { position })}
              >
                {t('Remove')}
              </button>
            </li>
          );
        })}
      </ul>

      {value.length < MAX_PHONE_NUMBERS ? (
        <button
          type="button"
          onClick={() => onChange([...value, { number: '', label: 'MOBILE' }])}
          className="mt-3 rounded-md px-2 py-1.5 text-base font-semibold text-indigo-700 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 min-h-[44px] min-w-[44px]"
        >
          {value.length ? t('Add another number') : t('Add a phone number')}
        </button>
      ) : (
        <p className="mt-3 text-base text-gray-500">
          {t('You can have up to {{max}} numbers.', { max: MAX_PHONE_NUMBERS })}
        </p>
      )}
    </fieldset>
  );
}

export default PhoneNumberList;
